<template>
    <div class="chrome-root" :class="{ 'is-expanded': isExpanded }">
        <AppHeader
            :show-bell="user.user_info_loaded && currentSection !== 'conciliador'"
            :has-new-docs="hasNewDocs"
            @bell-click="openLastDocuments"
        />

        <aside
            id="sidebar-rail"
            class="sidebar"
            :class="{ 'is-expanded': isExpanded }"
            aria-label="Navegación principal"
            @keydown.esc="onEsc"
        >
            <div class="sidebar-top">
                <button
                    ref="toggle"
                    type="button"
                    class="toggle"
                    :class="{ 'is-active': isExpanded }"
                    :aria-expanded="String(isExpanded)"
                    aria-controls="sidebar-rail"
                    aria-label="Alternar menú"
                    @click="toggleExpanded"
                >
                    <span class="toggle-bar"></span>
                    <span class="toggle-bar"></span>
                    <span class="toggle-bar"></span>
                </button>
            </div>

            <div
                v-if="!user.user_info_loaded"
                class="nav-list nav-skeleton"
                aria-hidden="true"
                aria-busy="true"
            >
                <div
                    v-for="n in 6"
                    :key="n"
                    class="nav-skeleton-row"
                >
                    <span class="nav-skeleton-icon"></span>
                    <span class="nav-skeleton-label"></span>
                </div>
            </div>

            <div v-else class="nav-list">
                <section
                    v-for="group in visibleGroups"
                    :key="group.id"
                    class="nav-group"
                >
                    <div v-show="isExpanded" class="nav-eyebrow">{{ group.label }}</div>
                    <ul class="nav-items">
                        <li
                            v-for="item in group.items"
                            :key="item.id"
                            class="nav-li"
                        >
                            <SidebarNavItem
                                :label="item.label"
                                :badge="item.badge || ''"
                                :icon="item.icon"
                                :active="currentSection === item.id"
                                :expanded="isExpanded"
                                :loading="item.requireChartLoaded && !chartLoaded"
                                :has-children="!!item.children"
                                :child-expanded="expandedIds.includes(item.id)"
                                :flyout-open="flyoutItemId === item.id"
                                @click="onParentClick(item, $event)"
                            />

                            <transition
                                name="accordion"
                                @before-enter="onAccordionBeforeEnter"
                                @enter="onAccordionEnter"
                                @after-enter="onAccordionAfterEnter"
                                @leave="onAccordionLeave"
                            >
                                <ul
                                    v-if="isExpanded && item.children && expandedIds.includes(item.id)"
                                    class="nav-sublist"
                                >
                                    <li
                                        v-for="child in item.children"
                                        :key="child.id"
                                        class="nav-sub-li"
                                    >
                                        <SidebarNavSubItem
                                            :label="child.label"
                                            :icon="child.icon"
                                            :active="currentSection === item.id && currentTab === child.id"
                                            @click="onChildClick(item, child)"
                                        />
                                    </li>
                                </ul>
                            </transition>
                        </li>
                    </ul>
                </section>
            </div>

            <SidebarUserSection
                :user="user"
                :expanded="isExpanded"
                :menu-open="isUserMenuOpen"
                @toggle="onUserToggle"
            />

            <SidebarFlyout
                :show="!!flyoutItem && !isExpanded"
                anchor-mode="top"
                :anchor-value="flyoutY"
                :title="flyoutItem ? flyoutItem.label : ''"
                :title-badge="flyoutItem && flyoutItem.badge ? flyoutItem.badge : ''"
                :aria-label="flyoutItem ? flyoutItem.label : ''"
                exclude-selector=".nav-item"
                @close="closeItemFlyout"
            >
                <ul v-if="flyoutItem" class="flyout-list">
                    <li
                        v-for="child in flyoutItem.children"
                        :key="child.id"
                        class="flyout-li"
                    >
                        <SidebarNavSubItem
                            :label="child.label"
                            :icon="child.icon"
                            :active="currentSection === flyoutItem.id && currentTab === child.id"
                            role="menuitem"
                            @click="onChildClick(flyoutItem, child)"
                        />
                    </li>
                </ul>
            </SidebarFlyout>

            <SidebarFlyout
                :show="isUserMenuOpen && !isExpanded"
                anchor-mode="bottom"
                :anchor-value="userFlyoutBottom"
                aria-label="Menú de usuario"
                :chrome="false"
                exclude-selector=".user-trigger"
                @close="closeUserMenu"
                @click="closeUserMenu"
            >
                <UserMenu :user="user" />
            </SidebarFlyout>
        </aside>
    </div>
</template>

<script>
import AppHeader from "@/components/layout/AppHeader.vue";
import SidebarFlyout from "@/components/layout/SidebarFlyout.vue";
import SidebarNavItem from "@/components/layout/SidebarNavItem.vue";
import SidebarNavSubItem from "@/components/layout/SidebarNavSubItem.vue";
import SidebarUserSection from "@/components/layout/SidebarUserSection.vue";
import UserMenu from "@/components/dashboard/UserMenu";
import BodyLastDocuments from "@/components/classification/lastDocuments/BodyLastDocuments";
import HeaderLastDocuments from "@/components/classification/lastDocuments/HeaderLastDocuments";
import ModalFullHeightRight from "@/components/modals/ModalFullHeightRight";
import { NAV_GROUPS, FLAT_ITEMS, SISTEMA_ALLOWED_EMAILS } from "@/components/layout/navConfig";
import { useAccordionTransition } from "@/composables/useAccordionTransition";

// Mapeo de allowlists por nombre (referencia simbólica en navConfig). Si
// agregamos más allowlists (e.g., DEV_ALLOWED_EMAILS), las registramos acá.
const EMAIL_ALLOWLISTS = {
    SISTEMA_ALLOWED_EMAILS,
};
import { mapActions, mapState } from "vuex";

export default {
    name: "MainLayout",
    components: {
        AppHeader,
        SidebarFlyout,
        SidebarNavItem,
        SidebarNavSubItem,
        SidebarUserSection,
        UserMenu,
    },
    props: {
        currentSection: {
            type: String,
            default: "",
        },
        chartLoaded: {
            type: Boolean,
            default: false,
        },
    },
    setup() {
        // Sólo accordion JS hooks via composable. State de flyouts vive en
        // data()/methods abajo — Vue 2 Options API tiene reactividad 100%
        // confiable sin los gotchas de auto-unwrap de refs en composition-api.
        const accordion = useAccordionTransition();
        return {
            onAccordionBeforeEnter: accordion.onBeforeEnter,
            onAccordionEnter: accordion.onEnter,
            onAccordionAfterEnter: accordion.onAfterEnter,
            onAccordionLeave: accordion.onLeave,
        };
    },
    data() {
        return {
            isExpanded: false,
            clicksEnabled: false,
            expandedIds: [],
            // State de los flyouts (collapsed mode). En expanded usamos
            // acordeón inline + user-menu inline; estos refs no aplican.
            isUserMenuOpen: false,
            flyoutItemId: null,
            flyoutY: 0,
            userFlyoutBottom: 12,
        };
    },
    computed: {
        ...mapState("userAuth", ["user"]),
        ...mapState("classification", ["hasNewDocs"]),
        currentTab() {
            // Si la URL trae ?tab=X explícito, ese es el tab activo.
            if (this.$route.query.tab) return this.$route.query.tab;
            // Fallback: cuando la sección activa tiene children pero no hay
            // tab en URL, asumimos el primer child como tab implícito. Los
            // views internos (e.g., DashboardReports) hacen el mismo default
            // — así sidebar y view quedan sincronizados visualmente.
            const item = FLAT_ITEMS.find((i) => i.id === this.currentSection);
            if (item && item.children && item.children.length > 0) {
                return item.children[0].id;
            }
            return null;
        },
        visibleGroups() {
            return NAV_GROUPS
                .filter((g) => this.groupVisibleForUser(g))
                .map((g) => ({
                    ...g,
                    items: g.items.filter(
                        (it) => it.flag === null || this.user[it.flag]
                    ),
                }))
                .filter((g) => g.items.length > 0);
        },
        flyoutItem() {
            if (!this.flyoutItemId) return null;
            return FLAT_ITEMS.find((i) => i.id === this.flyoutItemId) || null;
        },
    },
    watch: {
        chartLoaded: {
            handler(newValue) {
                if (newValue) {
                    this.clicksEnabled = true;
                }
            },
            immediate: true,
        },
        currentSection: {
            handler(section) {
                if (!section) return;
                const item = FLAT_ITEMS.find((i) => i.id === section);
                if (item && item.children && !this.expandedIds.includes(section)) {
                    this.expandedIds.push(section);
                }
            },
            immediate: true,
        },
        isExpanded: {
            handler(expanded) {
                // Publicamos el ancho para que main.dashboard empuje su
                // padding-left (modelo push, no overlay).
                document.documentElement.style.setProperty(
                    "--app-sidebar-w",
                    expanded ? "280px" : "64px"
                );
                if (!expanded) {
                    this.closeUserMenu();
                } else {
                    // Al expandir, el flyout deja de tener sentido — la
                    // acordeón inline cubre la misma función.
                    this.closeItemFlyout();
                }
            },
            immediate: true,
        },
    },
    beforeDestroy() {
        // Soltamos el var de ancho para que vistas sin Sidebar (login)
        // no queden con padding-left fantasma. Los click-outside listeners
        // se auto-limpian dentro de SidebarFlyout via su composable.
        document.documentElement.style.removeProperty("--app-sidebar-w");
    },
    methods: {
        ...mapActions("classification", ["getIfHasNewDocs"]),
        toggleExpanded() {
            this.isExpanded = !this.isExpanded;
        },
        groupVisibleForUser(group) {
            // Si el grupo declara requireEmailIn, sólo es visible para los
            // emails listados en esa allowlist. Sin requireEmailIn, todos.
            if (!group.requireEmailIn) return true;
            const allowlist = EMAIL_ALLOWLISTS[group.requireEmailIn] || [];
            const email = this.user && this.user.email;
            return Boolean(email) && allowlist.includes(email);
        },
        // === Flyout state (collapsed mode) — antes en useSidebarFlyouts ===
        openItemFlyout(item, triggerEl) {
            if (this.flyoutItemId === item.id) {
                this.closeItemFlyout();
                return;
            }
            // Sólo un overlay abierto a la vez.
            if (this.isUserMenuOpen) this.closeUserMenu();
            const rect = triggerEl.getBoundingClientRect();
            this.flyoutY = rect.top;
            this.flyoutItemId = item.id;
        },
        closeItemFlyout() {
            this.flyoutItemId = null;
        },
        openUserMenu(triggerEl) {
            this.closeItemFlyout();
            if (triggerEl) {
                // Anclamos el bottom al bottom del trigger para que el flyout
                // crezca hacia arriba sin desbordarse por debajo del viewport.
                const rect = triggerEl.getBoundingClientRect();
                this.userFlyoutBottom = Math.max(12, window.innerHeight - rect.bottom);
            }
            this.isUserMenuOpen = true;
        },
        closeUserMenu() {
            this.isUserMenuOpen = false;
        },
        toggleUserMenu(triggerEl) {
            if (this.isUserMenuOpen) this.closeUserMenu();
            else this.openUserMenu(triggerEl);
        },
        // === Event handlers ===
        onUserToggle(triggerEl) {
            // SidebarUserSection emite el button element directo (no el event).
            // Expanded: sin positioning. Collapsed: el toggle computa posición.
            this.toggleUserMenu(this.isExpanded ? null : triggerEl);
        },
        onParentClick(item, triggerEl) {
            // SidebarNavItem emite el button element directo (no el event)
            // — event.currentTarget queda null al atravesar el $emit chain.
            if (item.requireChartLoaded && !this.chartLoaded) return;
            if (!this.clicksEnabled) return;

            // Collapsed: el parent con children abre flyout sin navegar.
            if (item.children && !this.isExpanded) {
                this.openItemFlyout(item, triggerEl);
                return;
            }

            if (item.children) {
                // Si ya estás en esa sección, click sólo toggle-ea la acordeón
                // (no re-navega — el user no pierde su sub-tab).
                if (this.currentSection === item.id) {
                    this.toggleAccordion(item.id);
                } else {
                    this.$emit("change-section", item.id, null);
                    this.expandAccordion(item.id);
                }
                return;
            }

            this.$emit("change-section", item.id, null);
            this.closeItemFlyout();
        },
        expandAccordion(id) {
            if (!this.expandedIds.includes(id)) {
                this.expandedIds.push(id);
            }
        },
        toggleAccordion(id) {
            const idx = this.expandedIds.indexOf(id);
            if (idx >= 0) {
                this.expandedIds.splice(idx, 1);
            } else {
                this.expandedIds.push(id);
            }
        },
        onChildClick(parent, child) {
            if (!this.clicksEnabled) return;
            this.$emit("change-section", parent.id, child.id);
            this.closeItemFlyout();
        },
        onEsc() {
            // Cascada: cerrar overlays uno a uno antes de colapsar el sidebar.
            if (this.isUserMenuOpen) {
                this.closeUserMenu();
                return;
            }
            if (this.flyoutItemId) {
                this.closeItemFlyout();
                return;
            }
            if (this.isExpanded) {
                this.isExpanded = false;
            }
        },
        openLastDocuments() {
            this.closeItemFlyout();
            this.$buefy.modal.open({
                parent: this,
                hasModalCard: true,
                customClass: "classify_modal_width",
                canCancel: ["escape", "outside"],
                component: ModalFullHeightRight,
                props: {
                    headerComp: HeaderLastDocuments,
                    bodyComp: BodyLastDocuments,
                    headerProps: null,
                    bodyProps: null,
                },
                events: {
                    closed: this.getIfHasNewDocs,
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.chrome-root {
    // Tokens scope-componente, vocabulario ledger.
    --sidebar-paper: #ffffff;
    --sidebar-rule: rgba(16, 24, 40, 0.08);
    --sidebar-ink: #1d2939;
    --sidebar-ink-muted: #667085;
    --sidebar-ink-faint: #98a2b3;
    --sidebar-accent: #0d5154;
    --sidebar-accent-wash: rgba(13, 81, 84, 0.06);
    --sidebar-hover: rgba(16, 24, 40, 0.03);

    --sidebar-radius: 6px;
    --sidebar-w-collapsed: 64px;
    --sidebar-w-expanded: 280px;
    --sidebar-icon-size: 20px;
    --chrome-h: 56px;

    font-family: Inter, "Open Sans", sans-serif;
    color: var(--sidebar-ink);
}

.sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 70;
    width: var(--sidebar-w-collapsed);
    background-color: var(--sidebar-paper);
    border-right: 1px solid var(--sidebar-rule);
    display: flex;
    flex-direction: column;
    overflow: visible;
    transition: width var(--app-transition-base);

    &.is-expanded {
        width: var(--sidebar-w-expanded);
    }
}

.sidebar-top {
    height: var(--chrome-h);
    display: flex;
    align-items: center;
    padding: 0 14px;
    flex-shrink: 0;
}

.toggle {
    width: 36px;
    height: 36px;
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background-color: transparent;
    border: 1px solid var(--sidebar-rule);
    border-radius: var(--sidebar-radius);
    cursor: pointer;
    transition: background-color var(--app-transition-fast),
        border-color var(--app-transition-fast);

    &:hover {
        background-color: var(--sidebar-hover);
        border-color: rgba(16, 24, 40, 0.16);
    }

    &:focus-visible {
        outline: 2px solid var(--sidebar-accent);
        outline-offset: 2px;
    }
}

.toggle-bar {
    display: block;
    width: 16px;
    height: 1.5px;
    background-color: var(--sidebar-ink);
    border-radius: 1px;
    transition: transform var(--app-transition-base),
        opacity var(--app-transition-base);
}

.toggle.is-active .toggle-bar:nth-child(1) {
    transform: translateY(5.5px) rotate(45deg);
}
.toggle.is-active .toggle-bar:nth-child(2) {
    opacity: 0;
}
.toggle.is-active .toggle-bar:nth-child(3) {
    transform: translateY(-5.5px) rotate(-45deg);
}

.nav-list {
    flex: 1;
    // Collapsed: overflow visible para que tooltips escapen el contenedor.
    // Expanded: overflow-y auto en regla más específica abajo.
    overflow: visible;
    padding: 8px 0 12px;
}

.sidebar.is-expanded .nav-list {
    overflow-y: auto;
    overflow-x: hidden;
}

.nav-group {
    padding: 0 8px;

    & + & {
        margin-top: 4px;
        padding-top: 12px;
        border-top: 1px solid var(--sidebar-rule);
    }
}

.nav-eyebrow {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--sidebar-ink-faint);
    padding: 8px 10px 6px;
    white-space: nowrap;
}

.nav-items {
    list-style: none;
    margin: 0;
    padding: 0;
}

.nav-li {
    list-style: none;
}

// Wrapper de la lista de sub-items en el acordeón. El styling del botón en sí
// vive en SidebarNavSubItem (mismo componente que renderiza el flyout — DRY).
.nav-sublist {
    list-style: none;
    margin: 2px 0 6px 22px;
    padding: 2px 0 2px 10px;
    border-left: 1px solid var(--sidebar-rule);
    overflow: hidden;
}

.nav-sub-li {
    list-style: none;
}

.accordion-enter-active,
.accordion-leave-active {
    transition: height var(--app-transition-base),
        opacity var(--app-transition-base);
}

// Wrapper de la lista de items en el flyout (ul/li resets). El botón en sí
// es SidebarNavSubItem — mismo componente que renderiza el acordeón.
.flyout-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.flyout-li {
    list-style: none;
}

// Skeleton: imita el layout de nav-items reales para que la transición a
// items reales se sienta como un fade-in del contenido, no un layout shift.
.nav-skeleton {
    padding: 8px 0 12px;
}

.nav-skeleton-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0 8px;
    padding: 8px 12px;
    margin-bottom: 2px;
}

.nav-skeleton-icon {
    flex-shrink: 0;
    width: var(--sidebar-icon-size);
    height: var(--sidebar-icon-size);
    border-radius: 4px;
    background-color: rgba(16, 24, 40, 0.08);
    animation: nav-skeleton-pulse 1.4s ease-in-out infinite;
}

.nav-skeleton-label {
    flex: 1;
    height: 10px;
    border-radius: 4px;
    background-color: rgba(16, 24, 40, 0.08);
    opacity: 0;
    max-width: 0;
    overflow: hidden;
    transition: opacity var(--app-transition-fast) 0.05s,
        max-width var(--app-transition-base);
    animation: nav-skeleton-pulse 1.4s ease-in-out infinite;
}

.sidebar.is-expanded .nav-skeleton-label {
    opacity: 1;
    max-width: 160px;
}

@keyframes nav-skeleton-pulse {
    0%, 100% { background-color: rgba(16, 24, 40, 0.08); }
    50% { background-color: rgba(16, 24, 40, 0.04); }
}
</style>

<style lang="scss">
// Contratos publicados por el chrome:
//   --app-chrome-top: alto del header global
//   --app-sidebar-w: ancho actual del sidebar (varía 64 ↔ 280 según expand)
//   --app-transition-fast/base: escala de timing (hover/focus vs layout)
// Las vistas consumen estos vars para anclar sticky-headers, reservar
// espacio y mantener animaciones sincronizadas, sin acoplarse a valores
// hardcoded del Sidebar.
:root {
    --app-chrome-top: 56px;
    --app-sidebar-w: 64px;
    --app-transition-fast: 150ms ease;
    --app-transition-base: 200ms ease;
}

// Reserva permanente para el chrome global. `padding-left` se actualiza
// reactivamente cuando el sidebar expande/colapsa (el watcher publica el var),
// así el contenido se empuja en lugar de quedar tapado.
main.dashboard {
    padding-top: var(--app-chrome-top);
    padding-left: var(--app-sidebar-w);
    transition: padding-left var(--app-transition-base);
}

// Modales por encima del chrome: chrome usa z-index 60-75 (header / sidebar /
// flyout). Bulma/Buefy traen modales en z-index 40 por default — los subimos
// a 80 para que cualquier modal fullscreen tape correctamente el chrome.
.modal {
    z-index: 80;
}


</style>
